/**
 * NoteInfoModal - Reusable modal for displaying notes/explanations
 *
 * Used to show plan-level, week-level, and session-level notes
 * from AI-generated training plans.
 * Supports markdown formatting for rich text display.
 */

import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';

interface NoteInfoModalProps {
  title: string;
  note: string;
  iconSize?: 'small' | 'medium';
  iconColor?: 'primary' | 'action' | 'inherit';
}

/**
 * A clickable info icon that opens a modal with the note content.
 * Perfect for mobile and desktop - provides accessible, readable explanations.
 */
const NoteInfoModal: React.FC<NoteInfoModalProps> = ({
  title,
  note,
  iconSize = 'small',
  iconColor = 'action',
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click bubbling
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!note) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label={`View ${title.toLowerCase()} explanation`}
        sx={{
          p: iconSize === 'small' ? 0.25 : 0.5,
          ml: 0.5,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <InfoIcon
          fontSize={iconSize}
          color={iconColor}
          sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
        />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <InfoIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small" aria-label="Close note">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              '& p': {
                margin: '0.5em 0',
                lineHeight: 1.7,
              },
              '& strong': {
                fontWeight: 600,
              },
              '& ul, & ol': {
                margin: '0.5em 0',
                paddingLeft: '1.5em',
              },
              '& li': {
                marginBottom: '0.25em',
              },
              '& h1, & h2, & h3': {
                margin: '0.75em 0 0.5em',
                fontWeight: 600,
              },
              '& code': {
                backgroundColor: 'action.hover',
                padding: '0.1em 0.3em',
                borderRadius: '4px',
                fontSize: '0.9em',
              },
            }}
          >
            <ReactMarkdown>{note}</ReactMarkdown>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NoteInfoModal;
